<template>
  <v-card>
    <v-card-title class="pb-2">
      {{ draft.id ? 'Vorlage bearbeiten' : 'Neue Vorlage' }}
      <v-chip v-if="dirty" x-small label class="ml-2" color="orange" text-color="white">
        ungespeichert
      </v-chip>
      <v-spacer />
      <v-btn icon @click="$emit('cancel')" title="Schließen">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="draft.name"
            label="Anzeigename"
            outlined
            dense
            :error-messages="nameErrors"
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="draft.description"
            label="Beschreibung"
            outlined
            dense
            rows="2"
            no-resize
            hide-details
          />
        </v-col>
      </v-row>
      <v-divider class="my-4" />
      <v-tabs v-model="activeTab" class="mb-3">
        <v-tab>
          <v-icon left small>mdi-pencil</v-icon>
          Bearbeiten
        </v-tab>
        <v-tab :disabled="draft.nodes.length === 0">
          <v-icon left small>mdi-eye-outline</v-icon>
          Vorschau
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="activeTab">
        <v-tab-item :transition="false" :reverse-transition="false">
      <div class="d-flex align-center mb-2">
        <h3 class="text-subtitle-1">Knoten ({{ draft.nodes.length }})</h3>
        <v-spacer />
        <v-btn small text color="primary" @click="addNode">
          <v-icon left small>mdi-plus</v-icon>
          Knoten hinzufügen
        </v-btn>
      </div>
      <div v-if="draft.nodes.length === 0" class="text-caption grey--text mb-3">
        Noch keine Knoten. Eine Vorlage ohne Knoten kann angelegt, aber nicht sinnvoll als Ticket-Workflow verwendet werden.
      </div>
      <v-card
        v-for="(node, i) in draft.nodes"
        :key="i"
        outlined
        class="mb-3 node-card"
      >
        <div class="d-flex align-center px-3 py-2">
          <v-chip x-small label class="mr-2 step-chip">{{ i + 1 }}</v-chip>
          <v-text-field
            v-model="node.name"
            placeholder="Knoten-Name"
            outlined
            dense
            hide-details
            class="flex-grow-1"
          />
          <v-btn icon small :disabled="i === 0" @click="moveNode(i, -1)" title="Nach oben">
            <v-icon small>mdi-arrow-up</v-icon>
          </v-btn>
          <v-btn icon small :disabled="i === draft.nodes.length - 1" @click="moveNode(i, 1)" title="Nach unten">
            <v-icon small>mdi-arrow-down</v-icon>
          </v-btn>
          <v-btn icon small @click="removeNode(i)" title="Entfernen">
            <v-icon small>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
        <v-divider />
        <div class="px-3 py-3">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select
                v-model="node.assignee_type"
                :items="assigneeTypeOptions"
                label="Bearbeiter-Typ"
                outlined
                dense
                hide-details
                @change="onAssigneeTypeChange(node)"
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-autocomplete
                v-if="node.assignee_type === 'user'"
                v-model="node.assignee_user_id"
                :items="users"
                item-text="displayName"
                item-value="id"
                label="Benutzer"
                outlined
                dense
                hide-details
                clearable
                placeholder="Benutzer wählen…"
              />
              <v-autocomplete
                v-else
                v-model="node.assignee_group"
                :items="availableGroups"
                label="AD-Gruppe"
                outlined
                dense
                hide-details
                clearable
                placeholder="Gruppe wählen…"
              />
            </v-col>
          </v-row>
          <div class="d-flex align-center mt-3 mb-1">
            <span class="text-caption grey--text">Checkliste ({{ node.checklist.length }})</span>
            <v-spacer />
            <v-btn x-small text color="primary" @click="addChecklistItem(node)">
              <v-icon left x-small>mdi-plus</v-icon>
              Item
            </v-btn>
          </div>
          <div v-if="node.checklist.length === 0" class="text-caption grey--text mb-2">
            Keine Items. Checklisten sind optional und dienen nur als Leitfaden.
          </div>
          <div
            v-for="(item, j) in node.checklist"
            :key="j"
            class="d-flex align-center mb-1"
            style="gap: 4px;"
          >
            <v-text-field
              v-model="item.label"
              placeholder="Bezeichnung"
              outlined
              dense
              hide-details
              class="flex-grow-1"
            />
            <v-text-field
              v-model="item.icon"
              placeholder="mdi-icon (optional)"
              outlined
              dense
              hide-details
              style="max-width: 220px;"
              :prepend-inner-icon="item.icon || 'mdi-checkbox-blank-outline'"
            />
            <v-btn icon small @click="removeChecklistItem(node, j)" title="Item entfernen">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>
        </v-tab-item>
        <v-tab-item :transition="false" :reverse-transition="false">
          <div v-if="draft.nodes.length === 0" class="text-caption grey--text">
            Noch keine Knoten zum Anzeigen.
          </div>
          <div v-else class="wf-preview">
            <template v-for="(node, i) in draft.nodes">
              <div :key="'node-' + i" class="wf-step">
                <div class="wf-step-header">
                  <v-chip x-small label class="step-chip mr-2">{{ i + 1 }}</v-chip>
                  <span class="wf-step-name" :class="{ 'wf-step-name-empty': !node.name }">
                    {{ node.name || '(unbenannt)' }}
                  </span>
                </div>
                <div class="wf-step-meta">
                  <v-icon x-small class="mr-1">
                    {{ node.assignee_type === 'user' ? 'mdi-account' : 'mdi-account-group' }}
                  </v-icon>
                  <span :class="{ 'grey--text': !assigneeLabel(node) }">
                    {{ assigneeLabel(node) || 'kein Bearbeiter' }}
                  </span>
                </div>
                <div v-if="node.checklist.length" class="wf-checklist">
                  <div
                    v-for="(item, j) in node.checklist"
                    :key="j"
                    class="wf-checklist-item"
                  >
                    <v-icon x-small class="mr-1">{{ item.icon || 'mdi-checkbox-blank-outline' }}</v-icon>
                    <span :class="{ 'grey--text font-italic': !item.label }">
                      {{ item.label || '(unbenannt)' }}
                    </span>
                  </div>
                </div>
              </div>
              <v-icon
                v-if="i < draft.nodes.length - 1"
                :key="'arrow-' + i"
                class="wf-arrow"
                color="grey"
              >mdi-arrow-right</v-icon>
            </template>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
    <v-card-actions class="px-4 pb-4">
      <v-btn
        v-if="draft.id"
        color="error"
        text
        @click="$emit('delete', draft.id)"
      >
        <v-icon left small>mdi-delete</v-icon>
        Löschen
      </v-btn>
      <v-spacer />
      <v-btn text @click="$emit('cancel')">Abbrechen</v-btn>
      <v-btn
        color="primary"
        :disabled="!canSave"
        @click="$emit('save', draft)"
      >
        <v-icon left small>mdi-content-save</v-icon>
        Speichern
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "WorkflowEditor",
  props: {
    draft: { type: Object, required: true },
    dirty: { type: Boolean, default: false },
  },
  data: () => ({
    assigneeTypeOptions: [
      { text: "Benutzer", value: "user" },
      { text: "AD-Gruppe", value: "group" },
    ],
    activeTab: 0,
  }),
  computed: {
    users(){
      return this.$store.state.ticketsystem.users
    },
    availableGroups(){
      const set = new Set()
      for (const u of this.users) {
        if (Array.isArray(u.pms)) for (const p of u.pms) set.add(p)
      }
      return [...set].sort()
    },
    nameErrors(){
      return this.draft.name ? [] : ["Pflichtfeld"]
    },
    canSave(){
      return this.dirty && this.nameErrors.length === 0
    },
  },
  methods: {
    addNode(){
      this.draft.nodes.push({
        name: "",
        assignee_type: "group",
        assignee_user_id: null,
        assignee_group: null,
        checklist: [],
      })
    },
    removeNode(i){
      this.draft.nodes.splice(i, 1)
    },
    moveNode(i, dir){
      const j = i + dir
      if (j < 0 || j >= this.draft.nodes.length) return
      const tmp = this.draft.nodes[i]
      this.$set(this.draft.nodes, i, this.draft.nodes[j])
      this.$set(this.draft.nodes, j, tmp)
    },
    onAssigneeTypeChange(node){
      if (node.assignee_type === "user") {
        node.assignee_group = null
      } else {
        node.assignee_user_id = null
      }
    },
    addChecklistItem(node){
      node.checklist.push({ label: "", icon: "" })
    },
    removeChecklistItem(node, j){
      node.checklist.splice(j, 1)
    },
    assigneeLabel(node){
      if (node.assignee_type === "user") {
        if (!node.assignee_user_id) return ""
        const u = this.users.find(x => x.id === node.assignee_user_id)
        return u ? u.displayName : "Unbekannter Benutzer"
      }
      return node.assignee_group || ""
    },
  },
}
</script>

<style scoped>
.node-card {
  background: rgba(0, 0, 0, 0.02);
}
.step-chip.v-chip {
  background: rgba(123, 0, 0, 0.12) !important;
  color: #7b0000 !important;
  font-weight: 700;
}
.wf-preview {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  padding: 8px 4px;
}
.wf-step {
  flex: 0 0 auto;
  min-width: 180px;
  max-width: 240px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  background: #fff;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wf-step-header {
  display: flex;
  align-items: center;
}
.wf-step-name {
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wf-step-name-empty {
  font-style: italic;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 400;
}
.wf-step-meta {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wf-checklist {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.wf-checklist-item {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wf-checklist-item > span {
  overflow: hidden;
  text-overflow: ellipsis;
}
.wf-arrow.v-icon {
  align-self: center;
}
</style>
